import apiClient from './apiClient';

export interface ONRCCompany {
  codInmatriculare: string;
  denumire: string;
  cui: string;
  dataInmatriculare: string;
  formaJuridica: string;
  tara: string;
  judet: string;
  localitate: string;
  strada: string;
  numar: string;
  bloc: string;
  scara: string;
  etaj: string;
  apartament: string;
  codPostal: string;
  sector: string;
  completare: string;
  coduriCAENRelevante: string;
  toateCodurileCAEN: string;
}

export interface ONRCSearchParams {
  denumire?: string;
  cui?: string;
  judet?: string;
  localitate?: string;
  codCAEN?: string;
  limit?: number;
  offset?: number;
}

export interface ONRCSearchResponse {
  success: boolean;
  data: ONRCCompany[];
  total: number;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface ONRCStatistics {
  totalFirme: number;
  coduriCAENRelevante: string[];
  statisticiCAEN: Record<string, number>;
  statisticiJudete: Record<string, number>;
  dataGenerare: string;
}

export interface ONRCCaenCode {
  cod: string;
  descriere: string;
}

class ONRCService {
  private baseUrl = '/apiClient/v1/onrc';

  /**
   * Cauta firme in datele ONRC
   */
  async searchCompanies(params: ONRCSearchParams): Promise<ONRCSearchResponse> {
    const queryParams = new URLSearchParams();
    
    if (params.denumire) queryParams.append('denumire', params.denumire);
    if (params.cui) queryParams.append('cui', params.cui);
    if (params.judet) queryParams.append('judet', params.judet);
    if (params.localitate) queryParams.append('localitate', params.localitate);
    if (params.codCAEN) queryParams.append('codCAEN', params.codCAEN);
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.offset) queryParams.append('offset', params.offset.toString());

    const response = await apiClient.get(`${this.baseUrl}/companies?${queryParams}`);
    return response.data;
  }

  /**
   * Obtine o firma dupa CUI
   */
  async getCompanyByCui(cui: string): Promise<ONRCCompany | null> {
    try {
      const response = await this.searchCompanies({ cui, limit: 1 });
      return response.data.length > 0 ? response.data[0] : null;
    } catch (error) {
      console.error('Eroare la obtinerea firmei dupa CUI:', error);
      return null;
    }
  }

  /**
   * Obtine o firma dupa codul de inmatriculare
   */
  async getCompanyByCode(codInmatriculare: string): Promise<ONRCCompany | null> {
    try {
      const response = await apiClient.get(`${this.baseUrl}/companies/${codInmatriculare}`);
      return response.data.success ? response.data.data : null;
    } catch (error) {
      console.error('Eroare la obtinerea firmei dupa cod:', error);
      return null;
    }
  }

  /**
   * Obtine statistici despre datele ONRC
   */
  async getStatistics(): Promise<ONRCStatistics> {
    const response = await apiClient.get(`${this.baseUrl}/statistics`);
    return response.data.data;
  }

  /**
   * Obtine lista codurilor CAEN relevante
   */
  async getCaenCodes(): Promise<ONRCCaenCode[]> {
    const response = await apiClient.get(`${this.baseUrl}/caen-codes`);
    return response.data.data;
  }

  /**
   * Cauta firme dupa denumire (pentru auto-complete)
   */
  async searchCompaniesByName(denumire: string, limit: number = 10): Promise<ONRCCompany[]> {
    try {
      const response = await this.searchCompanies({ denumire, limit });
      return response.data;
    } catch (error) {
      console.error('Eroare la cautarea firmelor dupa denumire:', error);
      return [];
    }
  }

  /**
   * Populeaza datele unei clinici din informatiile ONRC
   */
  async populateClinicData(cui: string): Promise<Partial<ONRCCompany> | null> {
    try {
      const company = await this.getCompanyByCui(cui);
      if (!company) return null;

      return {
        denumire: company.denumire,
        cui: company.cui,
        formaJuridica: company.formaJuridica,
        judet: company.judet,
        localitate: company.localitate,
        strada: company.strada,
        numar: company.numar,
        bloc: company.bloc,
        scara: company.scara,
        etaj: company.etaj,
        apartament: company.apartament,
        codPostal: company.codPostal,
        sector: company.sector,
        completare: company.completare,
        coduriCAENRelevante: company.coduriCAENRelevante
      };
    } catch (error) {
      console.error('Eroare la popularea datelor clinicii:', error);
      return null;
    }
  }
}

export const onrcService = new ONRCService();
export default onrcService; 
